import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Copy,
  DownloadSimple,
  Check,
  CircleNotch,
  X,
  BookmarkSimple,
  Paperclip,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Finomító Panel - calm, single-thread document refinement workspace.
 *
 * Phases:
 *   idle:        intro block at top, optional selection workspace
 *   loading:     small spinner above input (initial refinement)
 *   suggestion:  Kijelölt rész + pills + Javasolt változat in workspace
 *   applied:     "Frissítve. Visszavonás" briefly replaces the intro
 */
const QUICK_ACTIONS = [
  {
    id: "confident",
    label: "Magabiztosabb",
    instr:
      "Rewrite the selected text with a more confident, decisive, assertive tone. Keep approximately the same length, key facts, and meaning. Do NOT shorten or summarize.",
  },
  {
    id: "human",
    label: "Emberibb",
    instr:
      "Rewrite the selected text to sound more natural and human - fewer corporate buzzwords, more conversational language. Keep approximately the same length and information.",
  },
  {
    id: "specific",
    label: "Konkrétabb",
    instr:
      "Rewrite the selected text to be more specific and concrete - replace vague phrases with examples, numbers, or tangible details. Keep approximately the same length.",
  },
  {
    id: "surprise",
    label: "Lepj meg!",
    instr: `Rewrite the selected text in a fresh, surprising way. Pick ONE of these stylistic packagings (random choice; vary across requests, do not default to the same one each time):

  1. Mini-story framing - open with a short narrative moment, then deliver the message
  2. Question-first framing - start with a rhetorical question that the message answers
  3. Metaphor or image - open with a vivid metaphor that frames the message
  4. Outcome-first framing - lead with the result/transformation, then explain how
  5. Contrast framing - set up an opposite or stereotype, then subvert it with the actual message
  6. Concrete moment - describe a specific situation that embodies the message

CRITICAL RULES - DO NOT VIOLATE:

A) PRESERVE the original
  - The original speaker (if first person "én/nekem", stay first person; if "te/neked", stay; if "mi/nekünk", stay)
  - The core message and key concepts - only the packaging changes
  - Approximately the same length (±20%)
  - The field's purpose (a tagline stays tagline-like; a sample text stays sample-text-like; an identity statement stays an identity statement; an avatar pain stays a pain; an offer pillar stays a pillar)

B) FIT THE FULL BLUEPRINT CONTEXT
  Before finalizing, verify the rewrite fits the broader blueprint provided to you. Use whichever of these signals are present in the blueprint payload:
  - Brand archetype (archetipus) and voice characteristics, if available
  - "ki_vagyok" / "ki_nem_vagyok" identity statements, if available
  - Tone descriptors (hangnem) and vibe scales (vibe_skala), if available
  - "kerulendo_szavak" list (Voice-only) - DO NOT use any of those words if the list is present
  - For AVATAR blueprints: the persona's pszichografia, fajdalomprofil, vagyak, ellenervek, kifogasok - keep the rewrite consistent with that persona's worldview, register, and emotional state
  - For OFFER blueprints: the program_nev, snapshot, modulok, ar_es_kockazatcsokkentes - keep the rewrite consistent with the offer's promise, audience, and pricing tier (do not over-promise on a low-ticket program; do not under-sell a premium one)
  - Overall stylistic patterns of any saved Brand Voice context

  The rewrite must sound like the SAME person/brand/persona. If the brand voice is professional and measured, do not produce a flippant rewrite. If the voice avoids hype, do not produce a hyped rewrite. If the avatar is a tired 35-year-old executive, do not write something that sounds like a 22-year-old influencer.

C) SANITY CHECK BEFORE RETURNING
  Mentally check:
    1. Does this fit naturally next to the surrounding fields?
    2. Would a reader recognize this is the same brand/person/persona?
    3. Does this avoid the listed forbidden words (if any)?
    4. Is this a meaningful refinement, not a random tangent?
  If any answer is "no", silently regenerate and return a fitting alternative.

DO NOT:
  - Change who is speaking
  - Substitute the message with a different one
  - Drop key terms or concepts from the original
  - Make it dramatically shorter or longer
  - Use words from the brand's "kerulendo_szavak" list (if present)
  - Produce something tonally inconsistent with the rest of the blueprint
  - Be clever at the expense of fit - clever-but-off-brand is worse than slightly-less-fresh but fitting

The goal: the user reads the rewrite and thinks "oh, that's a fresh way to say the same thing in my voice / in my avatar's voice / for my offer - I didn't think of that angle". The goal is NOT: "this is technically different but sounds like a different person/brand wrote it".`,
  },
];

export default function AgentOutputScreen({
  blueprintName,
  readyTitle,
  blueprint,
  renderBlueprint,
  isSaved,
  onSave,
  savingVault,
  onCopy,
  copied,
  onExportPdf,
  exporting,
  onRefine,
  onApply,
}) {
  const [phase, setPhase] = useState("idle");
  const [selection, setSelection] = useState(null);
  const [input, setInput] = useState("");
  const [chatFocused, setChatFocused] = useState(false);
  const [suggestedSnippet, setSuggestedSnippet] = useState("");
  const [suggestedBlueprint, setSuggestedBlueprint] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [lastInstruction, setLastInstruction] = useState(null);
  const [lastSelection, setLastSelection] = useState(null);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);

  const previewRef = useRef(null);
  const textareaRef = useRef(null);
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "0px";
    ta.style.height = Math.min(ta.scrollHeight, 100) + "px";
  }, [input]);

  // Capture selection from preview only when we're not already in a flow.
  useEffect(() => {
    const handleMouseUp = () => {
      if (phase === "loading" || phase === "suggestion") return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (text.length < 3) return;
      const root = previewRef.current;
      if (!root) return;
      let node = sel.anchorNode;
      while (node && node !== root) node = node.parentNode;
      if (!node) return;
      setSelection(text.slice(0, 600));
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [phase]);

  // Auto-dismiss applied confirmation after 6s
  useEffect(() => {
    if (phase !== "applied") return;
    const t = setTimeout(() => {
      setPhase("idle");
      setUndoSnapshot(null);
    }, 6000);
    return () => clearTimeout(t);
  }, [phase]);

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  // Initial refinement (from idle → suggestion)
  const requestRefinement = async (instr) => {
    if (phase === "loading" || phase === "suggestion" || !instr.trim()) return;
    setPhase("loading");
    setError(null);
    setSuggestionError(false);
    setLastInstruction(instr);
    setLastSelection(selection);
    try {
      const { snippet, blueprint: refined } = await onRefine(instr, selection);
      const trimmed = (snippet || "").trim();
      setSuggestedSnippet(trimmed);
      setSuggestedBlueprint(refined);
      setSuggestionError(!trimmed);
      setPhase("suggestion");
      setInput("");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Finomítás sikertelen");
      setPhase("idle");
    }
  };

  // Re-run refinement (from inside the suggestion card) - asks for a
  // meaningfully different variation. Only the body of the card changes.
  const regenerateVariant = async () => {
    if (regenerating || !lastInstruction) return;
    setRegenerating(true);
    setSuggestionError(false);
    const previousSnippet = suggestedSnippet;
    const variantInstr =
      lastInstruction +
      "\n\nGenerate a meaningfully different variation than the previous suggestion." +
      (previousSnippet
        ? ` The previous suggestion was: "${previousSnippet}". Avoid repeating it.`
        : "");
    try {
      const { snippet, blueprint: refined } = await onRefine(variantInstr, lastSelection);
      const trimmed = (snippet || "").trim();
      setSuggestedSnippet(trimmed);
      setSuggestedBlueprint(refined);
      setSuggestionError(!trimmed);
    } catch (e) {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  // "Újrapróbálom" - run the same request again from the error state.
  const retryAfterError = async () => {
    if (regenerating || !lastInstruction) return;
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const { snippet, blueprint: refined } = await onRefine(lastInstruction, lastSelection);
      const trimmed = (snippet || "").trim();
      setSuggestedSnippet(trimmed);
      setSuggestedBlueprint(refined);
      setSuggestionError(!trimmed);
    } catch (e) {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const applySuggestion = async () => {
    if (!suggestedBlueprint || phase !== "suggestion" || regenerating) return;
    setPhase("loading");
    try {
      const previous = await onApply(suggestedBlueprint);
      setUndoSnapshot(previous);
      setSuggestedBlueprint(null);
      setSuggestedSnippet("");
      setSelection(null);
      setPhase("applied");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Alkalmazás sikertelen");
      setPhase("suggestion");
    }
  };

  const cancelSuggestion = () => {
    setSuggestedBlueprint(null);
    setSuggestedSnippet("");
    setSuggestionError(false);
    setRegenerating(false);
    setPhase("idle");
  };

  const undoApplied = async () => {
    if (!undoSnapshot) return;
    setPhase("loading");
    try {
      await onApply(undoSnapshot);
      setUndoSnapshot(null);
      setPhase("idle");
    } catch (e) {
      setError(e?.message || "Visszavonás sikertelen");
      setPhase("applied");
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (input.trim()) requestRefinement(input.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const placeholder = selection
    ? "Mit változtassak a kijelölt részen?"
    : "Mit szeretnél finomítani?";

  const inputDisabled = phase === "loading" || phase === "suggestion";
  const canSend = input.trim().length > 0 && !inputDisabled;

  return (
    <div className="flex flex-col h-full" style={{ background: "#0a0a12" }}>
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">

        {/* === LEFT: Finomító Panel === */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="finomito-panel"
        >
          {/* Top: intro / applied + active workspace wrapper */}
          <div
            className="flex-1 overflow-y-auto"
            style={{ padding: "32px 28px 14px" }}
            data-testid="finomito-content"
          >
            <AnimatePresence mode="wait">
              {phase === "applied" ? (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              ) : (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.22 }}
                >
                  <IntroBlock readyTitle={readyTitle} blueprintName={blueprintName} />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Active workspace - appears only when there's a selection or suggestion */}
            <AnimatePresence>
              {phase !== "applied" && (selection || phase === "suggestion") && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12,
                    padding: 14,
                    margin: "32px -8px 0",
                  }}
                  data-testid="workspace-wrapper"
                >
                  {/* 1. Selection display - always shown when there's a selection.
                       Card with X in idle, badge without X in suggestion phase. */}
                  {selection &&
                    (phase === "suggestion" ? (
                      <SelectionBadge text={selection} />
                    ) : (
                      <SelectionCard text={selection} onClear={clearSelection} />
                    ))}

                  {/* 2. Quick action pills - only when there's a selection.
                       Muted (opacity 0.5, no pointer-events) during suggestion. */}
                  {selection && (
                    <div
                      className="mex-pill-row"
                      style={{
                        display: "flex",
                        gap: 7,
                        flexWrap: "wrap",
                        marginBottom: 12,
                        opacity:
                          phase === "suggestion"
                            ? 0.5
                            : phase === "loading"
                            ? 0.6
                            : 1,
                        pointerEvents:
                          phase === "suggestion" || phase === "loading"
                            ? "none"
                            : "auto",
                        cursor: phase === "loading" ? "wait" : "default",
                      }}
                      data-testid="quick-actions"
                    >
                      {QUICK_ACTIONS.map((qa) => (
                        <button
                          key={qa.id}
                          onClick={() => requestRefinement(qa.instr)}
                          data-testid={`qa-${qa.id}`}
                          style={{
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                            background: "rgba(124,108,242,0.07)",
                            border: "1px solid rgba(124,108,242,0.18)",
                            color: "#d4ccff",
                            padding: "7px 12px",
                            borderRadius: 7,
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: "pointer",
                            transition:
                              "background 0.15s, border-color 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(124,108,242,0.12)";
                            e.currentTarget.style.borderColor =
                              "rgba(124,108,242,0.28)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(124,108,242,0.07)";
                            e.currentTarget.style.borderColor =
                              "rgba(124,108,242,0.18)";
                          }}
                        >
                          {qa.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* 3. Suggestion card - only in suggestion phase */}
                  {phase === "suggestion" && (
                    <SuggestionCard
                      snippet={suggestedSnippet}
                      isError={suggestionError}
                      regenerating={regenerating}
                      onApply={applySuggestion}
                      onRetryVariant={regenerateVariant}
                      onRetryAfterError={retryAfterError}
                      onCancel={cancelSuggestion}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {error && phase === "idle" && (
              <div className="mt-5 text-[12.5px]" style={{ color: "#e58a9c" }}>
                {error}
              </div>
            )}
          </div>

          {/* Mid zone - only the loading indicator now (selection/suggestion live above) */}
          <div data-testid="finomito-context-zone">
            <AnimatePresence mode="wait">
              {phase === "loading" && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ padding: "0 24px 8px" }}
                  data-testid="loading-block"
                >
                  <div
                    className="flex items-center gap-2.5"
                    style={{ fontSize: 13, color: "#a8a8c0" }}
                  >
                    <CircleNotch size={14} weight="bold" className="mex-spin" />
                    Finomítás folyamatban…
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom: input - cream tinted, paper-airplane send */}
          <form
            onSubmit={handleSubmit}
            style={{ padding: "12px 24px 24px" }}
            data-testid="finomito-input-form"
          >
            <div
              style={{
                background:
                  "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                border: `1px solid ${
                  chatFocused ? "rgba(243,238,228,0.24)" : "rgba(243,238,228,0.14)"
                }`,
                borderRadius: 12,
                padding: "5px 5px 5px 14px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: chatFocused
                  ? "0 4px 20px rgba(0,0,0,0.30), 0 0 0 3px rgba(124,108,242,0.10)"
                  : "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                transition: "border-color 0.15s, box-shadow 0.15s, opacity 0.15s",
                opacity: inputDisabled ? 0.5 : 1,
                pointerEvents: inputDisabled ? "none" : "auto",
              }}
            >
              <button
                type="button"
                aria-label="Csatolás"
                style={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  color: "#8a8a9c",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#a89bff")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#8a8a9c")}
                data-testid="finomito-attach"
              >
                <Paperclip size={14} weight="regular" />
              </button>

              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setChatFocused(true)}
                onBlur={() => setChatFocused(false)}
                rows={1}
                disabled={inputDisabled}
                placeholder={placeholder}
                data-testid="finomito-input"
                className="flex-1 outline-none resize-none mex-airy-input"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "white",
                  fontSize: 14,
                  padding: "8px 0",
                  minHeight: 20,
                  maxHeight: 100,
                  fontFamily: "inherit",
                }}
              />

              <button
                type="submit"
                disabled={!canSend}
                data-testid="finomito-send"
                aria-label="Küldés"
                style={{
                  width: 36,
                  height: 36,
                  background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                  color: "white",
                  borderRadius: 9,
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: canSend ? "pointer" : "not-allowed",
                  opacity: canSend ? 1 : 0.5,
                  boxShadow: canSend
                    ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                    : "none",
                  transition: "opacity 0.15s, box-shadow 0.15s",
                }}
              >
                {phase === "loading" ? (
                  <CircleNotch size={15} weight="bold" className="mex-spin" />
                ) : (
                  <PaperAirplaneIcon />
                )}
              </button>
            </div>

            <div
              className="flex justify-between"
              style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}
            >
              <span>
                Tipp: jelölj ki egy részt jobboldalt - a kérésed arra fog vonatkozni.
              </span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
        </div>

        {/* === RIGHT: cream Preview (always shows the saved/applied blueprint) === */}
        <div
          className="overflow-y-auto h-full"
          style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}
        >
          <div
            ref={previewRef}
            data-preview-theme="cream"
            data-testid="preview-panel"
            className="mr-auto"
            style={{
              background: "#f3eee4",
              color: "#0a0a12",
              borderRadius: 10,
              boxShadow:
                "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "32px 44px 56px",
              maxWidth: 860,
              marginLeft: 0,
              position: "relative",
            }}
          >
            <div
              className="flex justify-end items-center"
              style={{ gap: 6, marginBottom: 8, position: "relative", zIndex: 1 }}
              data-html2canvas-ignore="true"
            >
              <button
                onClick={onCopy}
                data-testid="copy-btn"
                title="Másolás formázással"
                aria-label="Másolás formázással"
                className="flex items-center justify-center"
                style={{
                  height: 32,
                  padding: copied ? "0 12px" : 0,
                  width: copied ? "auto" : 32,
                  background: copied ? "rgba(29,158,117,0.14)" : "rgba(124,108,242,0.06)",
                  border: copied
                    ? "1px solid rgba(29,158,117,0.32)"
                    : "1px solid rgba(124,108,242,0.16)",
                  color: copied ? "#0f6e56" : "#5e4cd8",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 500,
                  gap: 6,
                  whiteSpace: "nowrap",
                  transition: "all 180ms ease",
                }}
              >
                {copied ? (
                  <>
                    <Check size={13} weight="bold" />
                    Másolva
                  </>
                ) : (
                  <Copy size={13} weight="regular" />
                )}
              </button>
              <button
                onClick={onExportPdf}
                disabled={exporting}
                data-testid="export-pdf-btn"
                title="PDF / Nyomtatás"
                aria-label="PDF / Nyomtatás"
                className="flex items-center justify-center"
                style={{
                  width: 32, height: 32,
                  background: "rgba(124,108,242,0.06)",
                  border: "1px solid rgba(124,108,242,0.16)",
                  color: "#5e4cd8", borderRadius: 8,
                  cursor: exporting ? "wait" : "pointer",
                }}
              >
                {exporting ? (
                  <CircleNotch size={13} className="mex-spin" weight="bold" />
                ) : (
                  <DownloadSimple size={13} weight="regular" />
                )}
              </button>
              {isSaved ? (
                <button
                  onClick={onSave}
                  data-testid="asset-vault-saved-btn"
                  className="inline-flex items-center"
                  style={{
                    background: "rgba(29,158,117,0.14)",
                    border: "1px solid rgba(29,158,117,0.32)",
                    color: "#0f6e56", padding: "7px 13px",
                    borderRadius: 8, fontSize: 12, fontWeight: 500, gap: 6,
                    cursor: "pointer",
                  }}
                  title="Verzió mentése"
                >
                  <Check size={13} weight="bold" />
                  Mentve
                </button>
              ) : (
                <button
                  onClick={onSave}
                  disabled={savingVault}
                  data-testid="save-to-vault-btn"
                  className="inline-flex items-center"
                  style={{
                    background: "rgba(124,108,242,0.10)",
                    border: "1px solid rgba(124,108,242,0.28)",
                    color: "#5e4cd8", padding: "7px 13px",
                    borderRadius: 8, fontSize: 12, fontWeight: 500, gap: 6,
                    cursor: savingVault ? "wait" : "pointer",
                  }}
                >
                  {savingVault ? (
                    <CircleNotch size={13} weight="bold" className="mex-spin" />
                  ) : (
                    <BookmarkSimple size={13} weight="regular" />
                  )}
                  {savingVault ? "Mentés…" : "Mentés a Tudástárba"}
                </button>
              )}
            </div>

            <h2
              className="font-heading"
              style={{
                color: "#0a0a12", fontSize: 30, fontWeight: 600,
                letterSpacing: "-0.02em", lineHeight: 1.1,
                marginTop: 8, marginBottom: 24,
                position: "relative", zIndex: 1,
              }}
            >
              {blueprintName}
            </h2>

            <AnimatePresence mode="wait">
              <motion.div
                key={JSON.stringify(blueprint).slice(0, 64)}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                data-testid="blueprint-output"
                style={{ position: "relative", zIndex: 1 }}
              >
                {renderBlueprint(blueprint)}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Sub-components ---------------------------- */

function PaperAirplaneIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="white"
      stroke="white"
      strokeWidth="0.8"
      strokeLinejoin="round"
      style={{ transform: "rotate(-45deg)" }}
      aria-hidden="true"
    >
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

function IntroBlock({ readyTitle, blueprintName }) {
  const fallback = (() => {
    const noun = (blueprintName || "").replace(/\s*\(.*\)\s*$/, "").trim();
    return noun ? `Elkészült: ${noun}.` : "Elkészült a blueprint.";
  })();
  const title = readyTitle || fallback;

  return (
    <div data-testid="intro-block" style={{ maxWidth: 480 }}>
      <div
        className="font-heading"
        style={{
          fontSize: 28,
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: "-0.015em",
          color: "#ffffff",
          marginBottom: 18,
        }}
        data-testid="intro-title"
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 17,
          fontWeight: 400,
          lineHeight: 1.7,
          color: "#c0c0d0",
        }}
      >
        Olvasd át a jobb oldalon - ha minden rendben, mentsd el a Tudástárba, és
        bármelyik másik ügynök használhatja. Ha valamin finomítanál, jelölj ki
        egy részt a dokumentumban, vagy írd lent, mit változtassak.
      </div>
    </div>
  );
}

function SelectionCard({ text, onClear }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.04)",
        borderLeft: "2px solid rgba(124,108,242,0.40)",
        borderRadius: 8,
        padding: "11px 13px",
        marginBottom: 12,
      }}
      data-testid="selection-card"
    >
      <div className="flex items-start gap-2.5">
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontSize: 9,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              color: "#a89bff",
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Kijelölt rész
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#8a8a9c",
              fontStyle: "italic",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            “{text}”
          </div>
        </div>
        <button
          onClick={onClear}
          data-testid="clear-selection"
          className="shrink-0"
          style={{
            color: "#6a6a78",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 2,
            marginTop: 1,
          }}
          aria-label="Kijelölés törlése"
          onMouseEnter={(e) => (e.currentTarget.style.color = "white")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#6a6a78")}
        >
          <X size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function SelectionBadge({ text }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.04)",
        borderLeft: "2px solid rgba(124,108,242,0.40)",
        borderRadius: 8,
        padding: "11px 13px",
        marginBottom: 12,
      }}
      data-testid="selection-badge"
    >
      <div
        style={{
          fontSize: 9,
          textTransform: "uppercase",
          letterSpacing: "0.16em",
          color: "#a89bff",
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        Kijelölt rész
      </div>
      <div
        style={{
          fontSize: 13,
          color: "#8a8a9c",
          fontStyle: "italic",
          lineHeight: 1.5,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        “{text}”
      </div>
    </div>
  );
}

export const SUGGESTION_MAX_HEIGHT = 360;

export function SuggestionCard({
  snippet,
  isError,
  regenerating,
  onApply,
  onRetryVariant,
  onRetryAfterError,
  onCancel,
}) {
  const [expanded, setExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const bodyRef = useRef(null);

  // Measure intrinsic content height (snippet may be tall) to decide whether
  // to show the "Több" affordance.
  useLayoutEffect(() => {
    if (!bodyRef.current) return;
    const el = bodyRef.current;
    const h = el.scrollHeight;
    setNeedsExpand(h > SUGGESTION_MAX_HEIGHT);
  }, [snippet, regenerating, isError]);

  // Reset expansion when the snippet changes
  useEffect(() => {
    setExpanded(false);
  }, [snippet]);

  return (
    <div
      style={{
        background: "rgba(124,108,242,0.10)",
        border: "1px solid rgba(124,108,242,0.22)",
        borderRadius: 10,
        padding: 14,
      }}
      data-testid="suggestion-card"
    >
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.16em",
          color: "#a89bff",
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        Javasolt változat
      </div>

      {regenerating ? (
        <div
          className="flex flex-col items-center justify-center"
          style={{ gap: 8, padding: "24px 0", color: "#a89bff" }}
          data-testid="suggestion-regenerating"
        >
          <CircleNotch size={14} weight="bold" className="mex-spin" />
          <span style={{ fontSize: 13 }}>Új verzió készül…</span>
        </div>
      ) : isError ? (
        <>
          <div
            style={{
              fontSize: 15,
              color: "#c0c0d0",
              lineHeight: 1.6,
              marginBottom: 12,
            }}
            data-testid="suggestion-error"
          >
            Nem sikerült javaslatot készíteni. Próbáld újra más megfogalmazással.
          </div>
          <div className="flex flex-wrap items-center" style={{ gap: 5 }} data-testid="suggestion-actions">
            <button
              onClick={onRetryAfterError}
              data-testid="retry-after-error"
              style={{
                background: "rgba(124,108,242,0.15)",
                border: "1px solid rgba(124,108,242,0.30)",
                color: "#d4ccff",
                padding: "7px 14px",
                borderRadius: 7,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Újrapróbálom
            </button>
            <button
              onClick={onCancel}
              data-testid="cancel-suggestion"
              style={{
                background: "transparent",
                border: "none",
                color: "#7a7a95",
                padding: "7px 11px",
                fontSize: 11,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Mégsem
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ position: "relative" }}>
            <div
              ref={bodyRef}
              style={{
                fontSize: 15,
                color: "white",
                lineHeight: 1.65,
                whiteSpace: "pre-wrap",
                maxHeight:
                  needsExpand && !expanded ? SUGGESTION_MAX_HEIGHT : "none",
                overflow:
                  needsExpand && !expanded ? "hidden" : "visible",
              }}
              data-testid="suggestion-snippet"
            >
              {snippet}
            </div>
            {needsExpand && !expanded && (
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 50,
                  background:
                    "linear-gradient(180deg, transparent, rgba(20,20,30,0.95) 70%)",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>

          {needsExpand && (
            <button
              onClick={() => setExpanded((v) => !v)}
              data-testid="toggle-expand-suggestion"
              style={{
                fontSize: 13,
                color: "#a89bff",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                margin: "6px 0 10px 0",
                padding: 0,
              }}
            >
              {expanded ? "Kevesebb" : "Több"}
            </button>
          )}

          <div
            className="flex flex-wrap items-center"
            style={{ gap: 5, marginTop: needsExpand ? 0 : 12 }}
            data-testid="suggestion-actions"
          >
            <button
              onClick={onApply}
              data-testid="apply-suggestion"
              style={{
                background: "linear-gradient(135deg, #7c6cf2, #5e4cd8)",
                color: "white",
                padding: "6px 11px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 500,
                border: "none",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(124,108,242,0.3)",
              }}
            >
              Alkalmazom
            </button>
            <button
              onClick={onRetryVariant}
              data-testid="retry-variant"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#c0c0d0",
                padding: "6px 11px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Új verziót
            </button>
            <button
              onClick={onCancel}
              data-testid="cancel-suggestion"
              style={{
                background: "transparent",
                border: "none",
                color: "#7a7a95",
                padding: "6px 11px",
                fontSize: 11,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Mégsem
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function AppliedBlock({ onUndo, canUndo }) {
  return (
    <div className="flex items-center gap-3" data-testid="applied-block">
      <span
        style={{
          fontSize: 14,
          color: "#86b89e",
          fontWeight: 500,
          letterSpacing: "-0.005em",
        }}
      >
        Frissítve.
      </span>
      {canUndo && (
        <button
          onClick={onUndo}
          data-testid="undo-applied"
          style={{
            fontSize: 12.5,
            color: "#a8a8b8",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "4px 0",
            textDecoration: "underline",
            textDecorationColor: "rgba(255,255,255,0.18)",
            textUnderlineOffset: 4,
          }}
        >
          Visszavonás
        </button>
      )}
    </div>
  );
}
